/* eslint-disable camelcase */
import React, { useState, useRef, useEffect, useMemo, forwardRef, useImperativeHandle } from "react";
import "./IDLForm.css";
import { Accordion, Col, Row, Form } from "react-bootstrap";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { SingleCalendar } from "../../../../components";
import SignatureCanvas from "react-signature-canvas";
import { ReactComponent as UploadIcon } from "../../../../assets/images/icons/solar_upload-line-duotone.svg";
import axios from "axios";
import debounce from "lodash.debounce";
import { useTranslation } from "../../../../contexts/TranslationContext";
import Cookies from "js-cookie";
import { axiosGet } from "../../../../utils/apis/api";

const IDLForm = forwardRef(({ onChange = () => {}, showErrors = false, initialData = null,selectedDates ,url}, ref) => {
  const [previewImage, setPreviewImage] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const { t } = useTranslation();
  const Lang = Cookies.get("preferred_language") || "en";
  const [activeAccordionKeys, setActiveAccordionKeys] = useState(['0']);

  // Field to accordion mapping
  const fieldToAccordionMap = {
    // Personal Information
    full_name: '0',
    date_of_birth: '0',
    gender: '0',
    nationality: '0',
    passport_number: '0',
    phone: '0',
    email: '0',
    
    // IDL Application Details
    purpose_of_travel: '1',
    destination: '1',
    travel_start: '1',
    travel_end: '1',
    type_of_license: '1',
    existing_license_number: '1',
    issue_date_domestic: '1',
    expiry_date_domestic: '1',
    
    // Payment & Billing
    payment_method: '2',
    billing_address: '2',
    invoice_required: '2',
    
    // Declaration & Signature
    agree_terms: '5',
    signature: '5',
    
    // Files
    file_passport_copy: '4',
    file_license_copy: '4',
    file_photo: '4'
  };

  const purposeOptions = [
    { name: "Tourism", label: t("Tourism") },
    { name: "Business", label: t("Business") },
    { name: "Study", label: t("Study") },
    { name: "Relocation", label: t("Relocation") },
    { name: "Other", label: t("Other") },
  ];
  
  const licenseTypeOptions = [
    { name: "International Driving Permit", label: t("InternationalDrivingPermit(IDP)") },
    { name: "International Driving License", label: t("InternationalDrivingLicense(IDL)") },
  ];

  const paymentMethodOptions = [
    { name: "Credit Card", label: t("CreditCard") },
    { name: "PayPal", label: t("PayPal") },
    { name: "Bank Transfer", label: t("BankTransfer") },
  ];

  const [form, setForm] = useState({
    full_name: "",
    date_of_birth: null,
    gender: "",
    nationality: "",
    passport_number: "",
    phone: "",
    email: "",
    address: "",
    purpose_of_travel: null,
    destination: null,
    destination_name: "",
    travel_start: null,
    travel_end: null,
    type_of_license: null,
    existing_license_number: "",
    issue_date_domestic: null,
    expiry_date_domestic: null,
    payment_method: null,
    billing_address: "",
    invoice_required: "",
    flight_details: "",
    hotel_details: "",
    emergency_contact: "",
    files: {},
    agree_terms: false,
    signature: "",
  });

  const [errors, setErrors] = useState({});
  const inputRefs = useRef({});
  const sigPadRef = useRef();

  // Restore signature from initialData
  useEffect(() => {
    if (initialData?.signature && sigPadRef.current) {
      sigPadRef.current.fromDataURL(initialData.signature);
    }
  }, [initialData]);

  const [natQuery, setNatQuery] = useState("");
  const [natOptions, setNatOptions] = useState([]);
  const [natLoading, setNatLoading] = useState(false);

  // const fetchNationalities = useMemo(
  //   () =>
  //     debounce(async (q = "") => {
  //       try {
  //         setNatLoading(true);
  //         const params = q ? { search: q, limit: 20 } : { limit: 20 };
  //         const { data } = await axios.get(
  //           "https://viaggioerp.inter-netsys.com/api/method/alltimes.www.api.countries",
  //           { params }
  //         );
  //         setNatOptions(data.result.countries || []);
  //       } catch (e) {
  //         console.error(e);
  //       } finally {
  //         setNatLoading(false);
  //       }
  //     }, 300),
  //   []
  // );

  const fetchNationalities = useMemo(
    () =>
      debounce(async (q = "") => {
        try {
          setNatLoading(true);

          const params = q ? { search: q, limit: 20 } : { limit: 20 };

          const data = await axiosGet(
            "https://viaggioerp.inter-netsys.com/api/method/alltimes.www.api.countries",
            params
          );

          setNatOptions(data?.result?.countries || []);
        } catch (e) {
          console.error(e);
        } finally {
          setNatLoading(false);
        }
      }, 300),
    []
  );
  
  useEffect(() => {
    fetchNationalities(natQuery);
  }, [natQuery, fetchNationalities]);
  
  useEffect(() => () => fetchNationalities.cancel(), [fetchNationalities]);

  const [destQuery, setDestQuery] = useState("");
  const [destOptions, setDestOptions] = useState([]);
  const [destLoading, setDestLoading] = useState(false);

  // const fetchDestinations = useMemo(
  //   () =>
  //     debounce(async (q = "") => {
  //       try {
  //         setDestLoading(true);
  //         const baseParams = { location_type: "City", limit: 20 };
  //         const params = q.length >= 2 ? { ...baseParams, search: q } : baseParams;
  //         const { data } = await axios.get(
  //           "https://viaggioerp.inter-netsys.com/api/method/alltimes.www.api.locations",
  //           { params }
  //         );
  //         setDestOptions(data.result.locations || []);
  //       } catch (e) {
  //         console.error(e);
  //       } finally {
  //         setDestLoading(false);
  //       }
  //     }, 300),
  //   []
  // );

  const fetchDestinations = useMemo(
    () =>
      debounce(async (q = "") => {
        try {
          setDestLoading(true);

          const baseParams = { location_type: "City", limit: 20 };
          const params =
            q.length >= 2 ? { ...baseParams, search: q } : baseParams;

          const data = await axiosGet(
            "https://viaggioerp.inter-netsys.com/api/method/alltimes.www.api.locations",
            params
          );

          setDestOptions(data?.result?.locations || []);
        } catch (e) {
          console.error(e);
        } finally {
          setDestLoading(false);
        }
      }, 300),
    []
  );
  
  useEffect(() => {
    if (destQuery.length >= 2) fetchDestinations(destQuery);
  }, [destQuery, fetchDestinations]);
  
  useEffect(() => () => fetchDestinations.cancel(), [fetchDestinations]);

  const required = {
    full_name: t("FullNameIsRequired"),
    date_of_birth: t("DateOfBirthIsRequired"),
    gender: t("GenderIsRequired"),
    nationality: t("NationalityIsRequired"),
    passport_number: t("PassportNumberIsRequired"),
    phone: t("PhoneNumberIsRequired"),
    email: t("EmailAddressIsRequired"),
    purpose_of_travel: t("PurposeOfTravelIsRequired"),
    destination: t("DestinationCountryRequired"),
    travel_start: t("StartDateofTravelsRequired"),
    travel_end: t("EndDateOfTravelIsRequired"),
    type_of_license: t("TypeOfLicenseIsRequired"),
    existing_license_number: t("DomesticLicenseNumberIsRequired"),
    issue_date_domestic: t("IssueDateOfDomesticLicenseIsRequired"),
    expiry_date_domestic: t("ExpiryDateOfDomesticLicenseIsRequired"),
    payment_method: t("PaymentMethodIsRequired"),
    billing_address: t("BillingAddressIsRequired"),
    invoice_required: t("PleaseSpecifyIfInvoiceIsRequired"),
    agree_terms:  t("YouMustAgreeToTheTerms"),
    signature: t("SignatureIsRequired"),
  };

  const requiredFiles = {
    passport_copy: t("PassportCopyIsRequired"),
    license_copy: t("DomesticDrivingLicenceCopyIsRequired"),
    photo: t("PassportSizedPhotographIsRequired"),
  };

  const rangeErrors = {
    trip: t("EndDateMustBeAfterStartDate"),
    license: t("ExpiryDateMustBeAfterIssueDate"),
  };

  const validate = () => {
    const errs = {};
    Object.entries(required).forEach(([k, msg]) => {
      if (!form[k] || (typeof form[k] === "string" && !form[k].trim()))
        errs[k] = msg;
    });
    Object.entries(requiredFiles).forEach(([k, msg]) => {
      if (!form.files[k]) errs[`file_${k}`] = msg;
    });

    if (form.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email)) {
        errs.email = t("EmailValidation");
      }
    }
    if (form.phone) {
        const phoneRegex = /^\+?[0-9]{6,15}$/;
        if (!phoneRegex.test(form.phone))
          errs.phone = t("InvalidPhoneNumberFormat");
      }

    if (
      form.travel_start &&
      form.travel_end &&
      new Date(form.travel_start) > new Date(form.travel_end)
    ) {
      errs.travel_end = rangeErrors.trip;
    }

    if (
      form.issue_date_domestic &&
      form.expiry_date_domestic &&
      new Date(form.issue_date_domestic) > new Date(form.expiry_date_domestic)
    ) {
      errs.expiry_date_domestic = rangeErrors.license;
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  
  useEffect(() => {
    onChange(form, validate());
  }, [form]);

  const handleFileChange = (k, f) => {
    if (!f) return;
    setForm((p) => ({ ...p, files: { ...p.files, [k]: f } }));
    if (inputRefs.current[k]) inputRefs.current[k].value = null;
  };
  
  const handleFileRemove = (k) => {
    setForm((p) => {
      const files = { ...p.files };
      delete files[k];
      return { ...p, files };
    });
    if (inputRefs.current[k]) inputRefs.current[k].value = null;
  };
  
  const truncate = (n, max = 28) => 
    !n ? "" : n.length <= max ? n : `${n.slice(0, max - 6)}...${n.slice(-5)}`;
  
  const fileDisplayName = (f) => 
    typeof f === "string" ? f.split("/").pop() : f.name;
  
  const err = (f) => showErrors && errors[f];

  const Auto = ({ options, value, setValue, placeholder }) => (
    <Autocomplete
      fullWidth
      options={options}
      getOptionLabel={(o) => o.label}
      value={value ? options.find((o) => o.name === value) : null}
      onChange={(_, v) => setValue(v?.name || null)}
      sx={{
        ".MuiAutocomplete-inputRoot": {
          fontSize: "12px",
          flexDirection: Lang === "ar" ? "row-reverse" : "row",
        },
        ".MuiAutocomplete-endAdornment": {
          left: Lang === "ar" ? "9 !important" : "unset !important",
          right: Lang === "ar" ? "unset !important" : "9 !important",
        },
        ".MuiInputBase-input": {
          textAlign: Lang === "ar" ? "right" : "left",
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={placeholder || t("Select")}
          InputProps={{
            ...params.InputProps,
            style: {
              direction: Lang === "ar" ? "rtl" : "ltr",
            },
            sx: {
              backgroundColor: "#FFF",
              borderRadius: "6px",
              padding: "9px 15px !important",
              fontFamily: '"Dmsans400"',
              fontSize: "14px !important",
              fontWeight: 400,
              letterSpacing: "0.25px",
            },
          }}
        />
      )}
    />
  );

  useEffect(() => {
    if (!initialData) return;
    
    const toDate = (s) => (s ? new Date(s) : null);
    setForm({
      ...initialData,
      files: initialData.files || {},
      destination_name: initialData.destination_name || "",
      date_of_birth: toDate(initialData.date_of_birth),
      travel_start: toDate(initialData.travel_start),
      travel_end: toDate(initialData.travel_end),
      issue_date_domestic: toDate(initialData.issue_date_domestic),
      expiry_date_domestic: toDate(initialData.expiry_date_domestic),
    });
    onChange(initialData, true);
  }, [initialData]);

  const scrollToFirstError = () => {
    const firstErrorKey = Object.keys(errors)[0];
    if (firstErrorKey) {
      const accordionKey = fieldToAccordionMap[firstErrorKey];
      if (accordionKey && !activeAccordionKeys.includes(accordionKey)) {
        setActiveAccordionKeys(prev => [...prev, accordionKey]);
      }

      setTimeout(() => {
        const element = document.getElementById(`field-${firstErrorKey}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  };

  useImperativeHandle(ref, () => ({
    scrollToFirstError
  }));

  useEffect(() => {
    if (showErrors && Object.keys(errors).length > 0) {
      const errorAccordionKeys = Object.keys(errors)
        .map(field => fieldToAccordionMap[field])
        .filter(key => key !== undefined);
      
      setActiveAccordionKeys(prev => [
        ...prev,
        ...errorAccordionKeys.filter(key => !prev.includes(key))
      ]);
    }
  }, [showErrors, errors]);

  return (
    <Accordion 
      alwaysOpen 
      className="hotel-form-accordion add-service-form-accordion services-idl-form-accordion-container"
      activeKey={activeAccordionKeys}
      onSelect={(keys) => setActiveAccordionKeys(keys)}
    >
      {/* 1. Personal Information */}
      <Accordion.Item eventKey="0" className="hotel-form-accordion-item">
        <Accordion.Header className="hotel-form-accordion-header">
          1. {t("PersonalInformation")}
        </Accordion.Header>
        <Accordion.Body className="hotel-form-accordion-body">
          <Row>
            {/* Full name */}
            <Col xl={4} md={6}>
              <div className="hotel-form-input-container" id="field-full_name">
                <label className="hotel-form-input-label">
                  {t("FullNameAsPerPassport")} <span>*</span>
                </label>
                <input
                  className="hotel-form-input"
                  value={form.full_name}
                  onChange={(e) => setForm(p => ({ ...p, full_name: e.target.value }))}
                  placeholder={t("FullName")}
                />
                {err("full_name") && <div className="field-error">{errors.full_name}</div>}
              </div>
            </Col>

            {/* DOB */}
            <Col xl={4} md={6}>
              <div className="hotel-form-input-container" id="field-date_of_birth">
                <label className="hotel-form-input-label">
                  {t("DateOfBirth")} <span>*</span>
                </label>
                <SingleCalendar
                  value={form.date_of_birth}
                  onChange={(e) => setForm(p => ({ ...p, date_of_birth: e.value }))}
                  showTime={false}
                  placeholder={t("DateOfBirth")}
                />
                {err("date_of_birth") && <div className="field-error">{errors.date_of_birth}</div>}
              </div>
            </Col>

            {/* Gender */}
            <Col xl={4} md={6}>
              <div className="hotel-form-input-container" id="field-gender">
                <label className="hotel-form-input-label">
                  {t("Gender")} <span>*</span>
                </label>
                <Form.Select
                  size="sm"
                  value={form.gender}
                  onChange={(e) => setForm(p => ({ ...p, gender: e.target.value }))}
                  className="hotel-form-input select-dropdown"
                >
                  <option value="" disabled>{t("SelectGender")}</option>
                  <option value="Male">{t("Male")}</option>
                  <option value="Female">{t("Female")}</option>
                </Form.Select>
                {err("gender") && <div className="field-error">{errors.gender}</div>}
              </div>
            </Col>

            {/* Nationality */}
            <Col xl={4} md={6}>
              <div className="hotel-form-input-container" id="field-nationality">
                <label className="hotel-form-input-label">
                  {t("Nationality")} <span>*</span>
                </label>
                <Autocomplete
                  options={natOptions}
                  loading={natLoading}
                  getOptionLabel={(o) => o.name || ""}
                  value={natOptions.find((o) => o.name === form.nationality) || null}
                  onInputChange={(_, v) => setNatQuery(v)}
                  onChange={(_, v) => setForm(p => ({ ...p, nationality: v?.name || "" }))}
                  sx={{
                      ".MuiAutocomplete-inputRoot": {
                        fontSize: "12px",
                        flexDirection: Lang === "ar" ? "row-reverse" : "row",
                      },
                      ".MuiAutocomplete-endAdornment": {
                        left:
                          Lang === "ar" ? "9 !important" : "unset !important",
                        right:
                          Lang === "ar" ? "unset !important" : "9 !important",
                      },
                      ".MuiInputBase-input": {
                        textAlign: Lang === "ar" ? "right" : "left",
                      },
                  }}
                  renderInput={(p) => (
                    <TextField
                      {...p}
                      size="small"
                      placeholder={t("SelectNationality")}
                      InputProps={{
                          ...p.InputProps,
                          style: {
                            direction: Lang === "ar" ? "rtl" : "ltr",
                          },
                          sx: {
                            backgroundColor: "#FFF",
                            borderRadius: "6px",
                            padding: "9px 15px !important",
                            fontFamily: '"Dmsans400"',
                            fontSize: "14px !important",
                            fontWeight: 400,
                            letterSpacing: "0.25px",
                          },
                        }}
                    />
                  )}
                />
                {err("nationality") && <div className="field-error">{errors.nationality}</div>}
              </div>
            </Col>

            {/* Passport number */}
            <Col xl={4} md={6}>
              <div className="hotel-form-input-container" id="field-passport_number">
                <label className="hotel-form-input-label">
                  {t("PassportNumber")} <span>*</span>
                </label>
                <input
                  className="hotel-form-input"
                  value={form.passport_number}
                  onChange={(e) => setForm(p => ({ ...p, passport_number: e.target.value }))}
                  placeholder={t("PassportNumber")}
                />
                {err("passport_number") && <div className="field-error">{errors.passport_number}</div>}
              </div>
            </Col>

            {/* Phone */}
            <Col xl={4} md={6}>
              <div className="hotel-form-input-container" id="field-phone">
                <label className="hotel-form-input-label">
                  {t("ContactNumber")} <span>*</span>
                </label>
                <input
                  className="hotel-form-input"
                  value={form.phone}
                  onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder={t("ContactNumber")}
                />
                {err("phone") && <div className="field-error">{errors.phone}</div>}
              </div>
            </Col>

            {/* Email */}
            <Col xl={4} md={6}>
              <div className="hotel-form-input-container" id="field-email">
                <label className="hotel-form-input-label">
                  {t("Email")} <span>*</span>
                </label>
                <input
                  type="email"
                  className="hotel-form-input"
                  value={form.email}
                  onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                  placeholder={t("Email")}
                />
                {err("email") && <div className="field-error">{errors.email}</div>}
              </div>
            </Col>

            {/* Address */}
            <Col xl={4} md={6}>
              <div className="hotel-form-input-container">
                <label className="hotel-form-input-label">
                  {t("ResidentialAddress")}
                </label>
                <input
                  className="hotel-form-input"
                  value={form.address}
                  onChange={(e) => setForm(p => ({ ...p, address: e.target.value }))}
                  placeholder={t("ResidentialAddress")}
                />
              </div>
            </Col>
          </Row>
        </Accordion.Body>
      </Accordion.Item>

      {/* 2. IDL Application Details */}
      <Accordion.Item eventKey="1" className="hotel-form-accordion-item">
        <Accordion.Header className="hotel-form-accordion-header">
          2. {t("IdlApplicationDetails")}
        </Accordion.Header>
        <Accordion.Body className="hotel-form-accordion-body">
          <Row>
            {/* Purpose */}
            <Col xl={4} md={6}>
              <div className="hotel-form-input-container" id="field-purpose_of_travel">
                <label className="hotel-form-input-label">
                  {t("PurposeofTravel")} <span>*</span>
                </label>
                <Auto
                  options={purposeOptions}
                  value={form.purpose_of_travel}
                  setValue={(v) => setForm(p => ({ ...p, purpose_of_travel: v }))}
                />
                {err("purpose_of_travel") && <div className="field-error">{errors.purpose_of_travel}</div>}
              </div>
            </Col>

            {/* Destination */}
            <Col xl={4} md={6}>
              <div className="hotel-form-input-container" id="field-destination">
                <label className="hotel-form-input-label">
                  {t("DestinationCountryCountries")} <span>*</span>
                </label>
                <Autocomplete
                  options={destOptions}
                  loading={destLoading}
                  getOptionLabel={(o) => {
                    const city = (o.location_name || "").replace(/\s+/g, " ").trim();
                    const parent = (o.parent_location_name || "").replace(/\s+/g, " ").trim();
                    return parent ? `${city} (${parent})` : city;
                  }}
                  value={form.destination ? {
                    name: form.destination,
                    location_name: form.destination_name || "",
                  } : null}
                  onOpen={() => !destOptions.length && fetchDestinations()}
                  onInputChange={(_, v, r) => r === "input" && setDestQuery(v)}
                  onChange={(_, v) => setForm(p => ({
                    ...p,
                    destination: v?.name || null,
                    destination_name: v?.location_name || "",
                  }))}
                  sx={{
                      ".MuiAutocomplete-inputRoot": {
                        fontSize: "12px",
                        flexDirection: Lang === "ar" ? "row-reverse" : "row",
                      },
                      ".MuiAutocomplete-endAdornment": {
                        left:
                          Lang === "ar" ? "9 !important" : "unset !important",
                        right:
                          Lang === "ar" ? "unset !important" : "9 !important",
                      },
                      ".MuiInputBase-input": {
                        textAlign: Lang === "ar" ? "right" : "left",
                      },
                  }}
                  renderInput={(p) => (
                    <TextField
                      {...p}
                      size="small"
                      placeholder={t("SelectCountryOrCity")}
                      InputProps={{
                          ...p.InputProps,
                          style: {
                            direction: Lang === "ar" ? "rtl" : "ltr",
                          },
                          sx: {
                            backgroundColor: "#FFF",
                            borderRadius: "6px",
                            padding: "9px 15px !important",
                            fontFamily: '"Dmsans400"',
                            fontSize: "14px !important",
                            fontWeight: 400,
                            letterSpacing: "0.25px",
                          },
                    }}
                    />
                  )}
                />
                {err("destination") && <div className="field-error">{errors.destination}</div>}
              </div>
            </Col>

            {/* Travel dates */}
            <Col xl={4} md={6}>
              <div className="hotel-form-input-container" id="field-travel_start">
                <label className="hotel-form-input-label">
                  {t("TravelStartDate")} <span>*</span>
                </label>
                <SingleCalendar
                  value={form.travel_start}
                  onChange={(e) => setForm(p => ({ ...p, travel_start: e.value }))}
                  placeholder={t("TravelStartDate")}
                  min={new Date(selectedDates.startDate)}
                  max={
                      form.travel_end
                        ? new Date(
                            Math.min(
                              new Date(form.travel_end).getTime(),
                              new Date(selectedDates.endDate).getTime()
                            )
                          )
                        : new Date(selectedDates.endDate)
                  }
                  viewDate={new Date(selectedDates.startDate)}
                />
                {err("travel_start") && <div className="field-error">{errors.travel_start}</div>}
              </div>
            </Col>
            
            <Col xl={4} md={6}>
              <div className="hotel-form-input-container" id="field-travel_end">
                <label className="hotel-form-input-label">
                  {t("TravelEndDate")} <span>*</span>
                </label>
                <SingleCalendar
                  value={form.travel_end}
                  onChange={(e) => setForm(p => ({ ...p, travel_end: e.value }))}
                  placeholder={t("TravelEndDate")}
                  min={
                      form.travel_start
                        ? new Date(
                            Math.max(
                              new Date(form.travel_start).getTime(),
                              new Date(selectedDates.startDate).getTime()
                            )
                          )
                        : new Date(selectedDates.startDate)
                    }
                    max={new Date(selectedDates.endDate)}
                    viewDate={new Date(selectedDates.endDate)}
                />
                {err("travel_end") && <div className="field-error">{errors.travel_end}</div>}
              </div>
            </Col>

            {/* License type */}
            <Col xl={4} md={6}>
              <div className="hotel-form-input-container" id="field-type_of_license">
                <label className="hotel-form-input-label">
                  {t("TypeOfLicense")} <span>*</span>
                </label>
                <Auto
                  options={licenseTypeOptions}
                  value={form.type_of_license}
                  setValue={(v) => setForm(p => ({ ...p, type_of_license: v }))}
                />
                {err("type_of_license") && <div className="field-error">{errors.type_of_license}</div>}
              </div>
            </Col>

            {/* Existing licence number */}
            <Col xl={4} md={6}>
              <div className="hotel-form-input-container" id="field-existing_license_number">
                <label className="hotel-form-input-label">
                  {t("ExistingDrivingLicenseNumber")} ({t("CountryOfIssuance")}) <span>*</span>
                </label>
                <input
                  className="hotel-form-input"
                  value={form.existing_license_number}
                  onChange={(e) => setForm(p => ({
                    ...p,
                    existing_license_number: e.target.value,
                  }))}
                  placeholder={t("ExistingDrivingLicenseNumber")}
                />
                {err("existing_license_number") && <div className="field-error">{errors.existing_license_number}</div>}
              </div>
            </Col>

            {/* Issue / Expiry of domestic licence */}
            <Col xl={4} md={6}>
              <div className="hotel-form-input-container" id="field-issue_date_domestic">
                <label className="hotel-form-input-label">
                  {t("IssueDateOfDomesticDrivingLicense")} <span>*</span>
                </label>
                <SingleCalendar
                  value={form.issue_date_domestic}
                  onChange={(e) => setForm(p => ({ ...p, issue_date_domestic: e.value }))}
                  showTime={false}
                  placeholder={t("IssueDateOfDomesticDrivingLicense")}
                />
                {err("issue_date_domestic") && <div className="field-error">{errors.issue_date_domestic}</div>}
              </div>
            </Col>
            
            <Col xl={4} md={6}>
              <div className="hotel-form-input-container" id="field-expiry_date_domestic">
                <label className="hotel-form-input-label">
                  {t("ExpiryDateOfDomesticDrivingLicense")} <span>*</span>
                </label>
                <SingleCalendar
                  value={form.expiry_date_domestic}
                  onChange={(e) => setForm(p => ({ ...p, expiry_date_domestic: e.value }))}
                  showTime={false}
                  placeholder={t("ExpiryDateOfDomesticDrivingLicense")}
                />
                {err("expiry_date_domestic") && <div className="field-error">{errors.expiry_date_domestic}</div>}
              </div>
            </Col>
          </Row>
        </Accordion.Body>
      </Accordion.Item>

      {/* 3. Payment & Billing */}
      <Accordion.Item eventKey="2" className="hotel-form-accordion-item">
        <Accordion.Header className="hotel-form-accordion-header">
          3. {t("PaymentAndBillingInformation")}
        </Accordion.Header>
        <Accordion.Body className="hotel-form-accordion-body">
          <Row>
            {/* Payment method */}
            <Col xl={4} md={6}>
              <div className="hotel-form-input-container" id="field-payment_method">
                <label className="hotel-form-input-label">
                  {t("PaymentMethod")} <span>*</span>
                </label>
                <Autocomplete
                  options={paymentMethodOptions}
                  getOptionLabel={(o) => o.label || ""}
                  dir={Lang === "ar" ? "rtl" : "ltr"}
                  value={paymentMethodOptions.find(opt => opt.name === form.payment_method) || null}
                  onChange={(_, v) => setForm(p => ({ ...p, payment_method: v?.name || "" }))}
                  sx={{
                    ".MuiAutocomplete-inputRoot": {
                      fontSize: "12px",
                      flexDirection: Lang === "ar" ? "row-reverse" : "row",
                    },
                    ".MuiAutocomplete-endAdornment": {
                      left: Lang === "ar" ? "9 !important" : "unset !important",
                      right: Lang === "ar" ? "unset !important" : "9 !important",
                    },
                    ".MuiInputBase-input": {
                      textAlign: Lang === "ar" ? "right" : "left",
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder={t("Select")}
                      InputProps={{
                        ...params.InputProps,
                        style: { direction: Lang === "ar" ? "rtl" : "ltr" },
                        sx: {
                          backgroundColor: "#FFF",
                          borderRadius: "6px",
                          padding: "9px 15px !important",
                          fontFamily: '"Dmsans400"',
                          fontSize: "14px !important",
                          fontWeight: 400,
                          letterSpacing: "0.25px",
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      key={option.name}
                      style={{
                        fontFamily: Lang === "ar" ? "Almarai300" : "Dmsans400",
                        direction: Lang === "ar" ? "rtl" : "ltr",
                        textAlign: Lang === "ar" ? "right" : "left",
                      }}
                    >
                      {option.label}
                    </li>
                  )}
                />
                {err("payment_method") && <div className="field-error">{errors.payment_method}</div>}
              </div>
            </Col>

            {/* Billing address */}
            <Col xl={8} md={6}>
              <div className="hotel-form-input-container" id="field-billing_address">
                <label className="hotel-form-input-label">
                  {t("BillingAddress")} <span>*</span>
                </label>
                <input
                  className="hotel-form-input"
                  value={form.billing_address}
                  onChange={(e) => setForm(p => ({ ...p, billing_address: e.target.value }))}
                  placeholder={t("BillingAddress")}
                />
                {err("billing_address") && <div className="field-error">{errors.billing_address}</div>}
              </div>
            </Col>

            {/* Invoice required */}
            <Col xl={12}>
              <div className="add-service-form-checkbox-container" id="field-invoice_required">
                <div className="add-service-form-checkbox-title">
                  {t("InvoiceRequired")}<span style={{ color: "red" }}> *</span>
                </div>
                <Form className="add-service-form-checkboxes">
                  {["1", "0"].map((v) => (
                    <Form.Check
                      key={v}
                      inline
                      type="radio"
                      label={v === "1" ? t("Yes") : t("No")}
                      name="invoice"
                      value={v}
                      checked={form.invoice_required === v}
                      onChange={() => setForm(p => ({ ...p, invoice_required: v }))}
                      className="add-service-form-checkbox"
                    />
                  ))}
                </Form>
                {err("invoice_required") && <div className="field-error">{errors.invoice_required}</div>}
              </div>
            </Col>
          </Row>
        </Accordion.Body>
      </Accordion.Item>

      {/* 4. Travel & Contact */}
      <Accordion.Item eventKey="3" className="hotel-form-accordion-item">
        <Accordion.Header className="hotel-form-accordion-header">
          4. {t("TravelAndContactDetails")}
        </Accordion.Header>
        <Accordion.Body className="hotel-form-accordion-body">
          <Row>
            {[
              { key: "flight_details", label: t("FlightDetailsIfApplicable"), mandatory: 0 },
              { key: "hotel_details", label: `${t("HotelDetails")} (${t("IfApplicable")})`, mandatory: 0 },
              { key: "emergency_contact", label: t("EmergencyContactPersonAndRelationship"), mandatory: 1 },
            ].map(({ key, label, mandatory }) => (
              <Col xl={4} md={6} key={key}>
                <div className="hotel-form-input-container">
                  <label className="hotel-form-input-label">
                    {label} {mandatory ? <span>*</span> : null}
                  </label>
                  <input
                    className="hotel-form-input"
                    value={form[key]}
                    onChange={(e) => setForm(p => ({ ...p, [key]: e.target.value }))}
                    placeholder={label}
                  />
                </div>
              </Col>
            ))}
          </Row>
        </Accordion.Body>
      </Accordion.Item>

      {/* 5. Supporting Documents */}
      <Accordion.Item eventKey="4" className="hotel-form-accordion-item">
        <Accordion.Header className="hotel-form-accordion-header">
          5. {t("SupportedDocuments")} ({t("Uploads")})
        </Accordion.Header>
        <Accordion.Body className="hotel-form-accordion-body">
          <Row>
            {[
              { key: "passport_copy", label: t("PassportCopy"), mandatory: 1 },
              { key: "license_copy", label: t("DomesticDrivingLicenseCopyFrontAndBack"), mandatory: 1 },
              { key: "photo", label: t("PassportSizedPhotograph"), mandatory: 1 },
              { key: "address_proof", label: `${t("ProofOfAddress")} ${t("IfRequired")}`, mandatory: 0 },
              { key: "travel_insurance", label: t("TravelInsuranceIfRequired"), mandatory: 0 },
            ].map(({ key, label, mandatory }) => {
              const uid = `idl-file-${key}`;
              const file = form?.files[key];
              const isPdf =
                file?.name?.toLowerCase().endsWith(".pdf") ||
                (typeof file === "string" &&
                  file.toLowerCase().endsWith(".pdf"));
              return (
                <Col xl={6} md={6} key={key}>
                  <div
                    className="hotel-form-input-container"
                    id={`field-file_${key}`}
                  >
                    <label className="hotel-form-input-label">
                      {label} {mandatory ? <span>*</span> : null}
                    </label>

                    <div className="custom-upload-wrapper">
                      <input
                        // id={uid}
                        id={`file-${key}`}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        style={{ display: "none" }}
                        ref={(el) => (inputRefs.current[key] = el)}
                        onChange={(e) =>
                          handleFileChange(key, e.target.files[0])
                        }
                      />

                      {/* <label htmlFor={uid} className="custom-upload-box">
                        {!form.files?.[key] ? (
                          <>
                            <span className="upload-placeholder">
                              {t("Supported")}: JPEG, PNG, PDF
                            </span>
                            <UploadIcon className="upload-svg-icon" />
                          </>
                        ) : (
                          <>
                            <span className="upload-filename">
                              {truncate(fileDisplayName(form.files?.[key]))}
                            </span>
                            <span
                              className="upload-remove"
                              onClick={(e) => {
                                e.preventDefault();
                                handleFileRemove(key);
                              }}
                            >
                              ×
                            </span>
                          </>
                        )}
                      </label> */}

                      {file ? (
                        <div className="custom-upload-box">
                          <div className="file-info">
                            {isPdf ? (
                              <a
                                href={
                                  typeof file === "string"
                                    ? `${url}${file}`
                                    : URL.createObjectURL(file)
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="file-link"
                              >
                                {truncate(fileDisplayName(file))}
                                <span className="file-type-badge">PDF</span>
                              </a>
                            ) : (
                              <span className="file-name">
                                {truncate(fileDisplayName(file))}
                              </span>
                            )}
                          </div>

                          <div className="file-actions">
                            <button
                              type="button"
                              className="file-action-btn view-file-btn"
                              onClick={() => {
                                if (isPdf) {
                                  window.open(
                                    typeof file === "string"
                                      ? `${url}${file}`
                                      : URL.createObjectURL(file),
                                    "_blank"
                                  );
                                } else {
                                  const imageUrl =
                                    typeof file === "string"
                                      ? `${url}${file}`
                                      : URL.createObjectURL(file);
                                  setPreviewImage(imageUrl);
                                  setShowModal(true);
                                }
                              }}
                            >
                              {isPdf ? t("View") : t("Preview")}
                            </button>

                            <button
                              type="button"
                              className="file-action-btn remove-file-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                handleFileRemove(key);
                              }}
                            >
                              {t("Remove")}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <label
                          htmlFor={`file-${key}`}
                          className="custom-upload-box"
                        >
                          <span className="upload-placeholder">
                            {t("Supported")}: {t("Jpeg")}, {t("Png")},{" "}
                            {t("Pdf")}
                          </span>
                          <UploadIcon className="upload-svg-icon" />
                        </label>
                      )}
                    </div>

                    {err(`file_${key}`) && (
                      <div className="field-error" style={{ marginTop: 5 }}>
                        {errors[`file_${key}`]}
                      </div>
                    )}
                  </div>
                </Col>
              );
            })}
          </Row>
        </Accordion.Body>
      </Accordion.Item>

      {/* 6. Declaration & Signature */}
      <Accordion.Item eventKey="5" className="hotel-form-accordion-item">
        <Accordion.Header className="hotel-form-accordion-header">
          6. {t("DeclarationAndSignature")}
        </Accordion.Header>
        <Accordion.Body className="hotel-form-accordion-body">
          <Row>
            {/* Terms */}
            <Col xl={12}>
              <div className="hotel-form-input-container" id="field-agree_terms">
                <Form.Check
                  type="checkbox"
                  label={t("agreeHint")}
                  checked={form.agree_terms}
                  onChange={(e) => setForm(p => ({ ...p, agree_terms: e.target.checked }))}
                />
                {err("agree_terms") && <div className="field-error">{errors.agree_terms}</div>}
              </div>
            </Col>

            {/* Signature */}
            <Col xl={6}>
              <label className="hotel-form-input-label">
                {t("Signature")} <span>*</span>
              </label>
              <div className="signature-container" style={{ position: "relative" }} id="field-signature">
                <SignatureCanvas
                  ref={sigPadRef}
                  penColor="black"
                  canvasProps={{ width: 500, height: 150, className: "sigCanvas" }}
                  onEnd={() => setForm(p => ({ ...p, signature: sigPadRef.current.toDataURL() }))}
                />
                <button
                  type="button"
                  title="Clear"
                  onClick={() => {
                    sigPadRef.current.clear();
                    setForm(p => ({ ...p, signature: "" }));
                  }}
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 40,
                    background: "none",
                    border: "none",
                    fontSize: 18,
                  }}
                >
                  ⟳
                </button>
              </div>
              {err("signature") && <div className="field-error">{errors.signature}</div>}
            </Col>
          </Row>
        </Accordion.Body>
      </Accordion.Item>
       {previewImage && (
          <div
            className={`modal fade ${
              showModal ? "show d-block" : ""
            } service-files-modal`}
            tabIndex="-1"
            role="dialog"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            onClick={() => setShowModal(false)}
          >
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <button
                  type="button"
                  className="close"
                  onClick={() => setShowModal(false)}
                ></button>
                <div className="service-image-file">
                  <img
                    src={previewImage}
                    alt="preview"
                    style={{ maxWidth: "100%" }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
    </Accordion>
  );
});

export default IDLForm;